//import { getCurrentInstance } from "vue"; doing imports in libs is somehow bad for vue3
import { apiFetchJson } from "./api";

const DEFAULT_GS1_DIGITAL_LINK_BASE_URI = "https://id.gs1.org/";
const GS1_DEFAULT_AIS = ["01", "10", "17"];
const GS1_GTIN_QUALIFIER_ORDER = ["22", "10", "21", "235"];

function decycle(obj, stack = []) {
    if (!obj || typeof obj !== "object") return obj;

    if (stack.includes(obj)) return null;

    let s = stack.concat([obj]);

    return Array.isArray(obj)
        ? obj.map((x) => decycle(x, s))
        : Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, decycle(v, s)]));
}

function normalizeUserFromApi(user) {
    if (!user || typeof user !== "object") return user;
    const metadata = user.metadata && typeof user.metadata === "object" ? user.metadata : {};
    return {
        ...user,
        phoneNumber: user.phoneNumber ?? user.phone_number ?? "",
        ownerUserId: user.ownerUserId ?? user.owner_user_id ?? null,
        ownerGroupId: user.ownerGroupId ?? user.owner_group_id ?? null,
        userShortcode: user.userShortcode ?? user.user_shortcode ?? null,
        fname: metadata.fname ?? user.fname ?? "",
        lname: metadata.lname ?? user.lname ?? "",
    };
}

async function getUser(id) {
    try {
        const user = await apiFetchJson(`users/${encodeURIComponent(id)}`, {}, { auth: true });
        return normalizeUserFromApi(user);
    } catch (err) {
        console.log(`Error Loading User ${id} - ${err?.message || JSON.stringify(err)}`);
        return null;
    }
}

function getUserFullNameFromApi(user) {
    if (!user || typeof user !== "object") return "";
    const normalizedUser = normalizeUserFromApi(user);
    const fname = normalizedUser.fname ?? "";
    const lname = normalizedUser.lname ?? "";
    return `${fname} ${lname}`.trim();
}

function resolveGs1DigitalLinkUri(code) {
    if (!code || typeof code !== "object") return "";
    const explicitDigitalLinkUri = `${code.gs1?.digitalLinkUri ?? ""}`.trim();
    if (explicitDigitalLinkUri) return explicitDigitalLinkUri;
    const shortcode = code.shortcode ?? code.shortcodeid;
    return buildShortcodeBaseUri(shortcode);
}

function normalizeCodeFromApi(code) {
    if (!code || typeof code !== "object") return code;
    const metadata = code.metadata || {};
    const resolvedGs1DigitalLinkUri = resolveGs1DigitalLinkUri(code);
    const resolvedUpc = code.finalGtin ?? code.gtin ?? code.upc;
    return {
        ...code,
        owneruserid: code.userid ?? code.owneruserid,
        codeid: code.id ?? code.codeid,
        shortcodeid: code.shortcode ?? code.shortcodeid,
        BusName: code.title ?? code.BusName,
        description: code.description ?? metadata.description ?? code.Description ?? "",
        desturl: code.redirectUrl ?? code.desturl,
        redirecturl: code.redirectUrl ?? code.redirecturl,
        phone: code.phone ?? code.phone,
        location: code.address ?? code.location,
        selectedStyleSelectOption:
            metadata.selectedStyleSelectOption ?? code.selectedStyleSelectOption,
        selectedDpiSelectOption:
            metadata.selectedDpiSelectOption ?? code.selectedDpiSelectOption,
        sourceurl: metadata.sourceurl ?? code.sourceurl,
        createdate: code.createdAt ?? code.createdate,
        lastmodify: code.updatedAt ?? code.lastmodify,
        upcSupport: metadata.upcSupport ?? code.upcSupport ?? Boolean(resolvedUpc),
        gs1Support: metadata.gs1Support ?? code.gs1Support ?? Boolean(code.gs1),
        upc: resolvedUpc,
        calcCheckDigit:
            code.gtinAutoFixed ?? code.autoFixGtinCheckDigit ?? code.calcCheckDigit,
        gtinFormat: code.gtinFormat ?? metadata.gtinFormat ?? null,
        gs1:
            code.gs1 && typeof code.gs1 === "object"
                ? {
                    ...code.gs1,
                    digitalLinkUri: resolvedGs1DigitalLinkUri || code.gs1.digitalLinkUri,
                }
                : null,
        gs1Qr: code.gs1Qr && typeof code.gs1Qr === "object" ? code.gs1Qr : null,
        gs1DataMatrix:
            code.gs1DataMatrix && typeof code.gs1DataMatrix === "object"
                ? code.gs1DataMatrix
                : null,
        qr: code.qr && typeof code.qr === "object" ? code.qr : null,
        gtinOptions:
            code.gtinOptions && typeof code.gtinOptions === "object" ? code.gtinOptions : null,
        combinedBarcode:
            code.combinedBarcode && typeof code.combinedBarcode === "object"
                ? code.combinedBarcode
                : null,
    };
}

function createDefaultGs1AiEntries() {
    return GS1_DEFAULT_AIS.map((ai) => ({ ai, value: "", isDefault: true }));
}

function normalizeGs1AiEntries(entries) {
    if (!Array.isArray(entries)) return [];
    return entries
        .map((entry) => ({
            ai: `${entry?.ai ?? ""}`.trim(),
            value: `${entry?.value ?? ""}`.trim(),
            isDefault: Boolean(entry?.isDefault),
        }))
        .filter((entry) => entry.ai.length > 0);
}

function parseGs1ElementStringToAiEntries(elementString) {
    if (!elementString || typeof elementString !== "string") return [];
    const matches = [...elementString.matchAll(/\((\d{2,4})\)([^()]+)/g)];
    return matches.map((match) => ({
        ai: `${match[1] || ""}`.trim(),
        value: `${match[2] || ""}`.trim(),
        isDefault: GS1_DEFAULT_AIS.includes(`${match[1] || ""}`.trim()),
    }));
}

function getGs1AiEntries(code) {
    const existingEntries = normalizeGs1AiEntries(code?.gs1?.aiEntries || code?.gs1?.entries);
    if (existingEntries.length > 0) return existingEntries;
    return parseGs1ElementStringToAiEntries(code?.gs1?.elementString || "");
}

function buildGs1FormAiEntries(code) {
    const existingEntries = getGs1AiEntries(code);
    const byAi = new Map(existingEntries.map((entry) => [entry.ai, entry]));
    const defaults = createDefaultGs1AiEntries().map((entry) => ({
        ...entry,
        value: byAi.get(entry.ai)?.value || "",
    }));
    const extras = existingEntries.filter((entry) => !GS1_DEFAULT_AIS.includes(entry.ai));
    return defaults.concat(extras.map((entry) => ({ ...entry, isDefault: false })));
}

function buildGs1ElementString(aiEntries) {
    return normalizeGs1AiEntries(aiEntries)
        .filter((entry) => entry.value.length > 0)
        .map((entry) => `(${entry.ai})${entry.value}`)
        .join("");
}

function getApiOriginUrl() {
    const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;
    if (apiBaseUrl) {
        const parsedApiBaseUrl = new URL(apiBaseUrl);
        parsedApiBaseUrl.pathname = "/";
        parsedApiBaseUrl.search = "";
        parsedApiBaseUrl.hash = "";
        return parsedApiBaseUrl.toString();
    }
    return "https://qrexl.ink/";
}

function buildShortcodeBaseUri(shortcode) {
    const normalizedShortcode = `${shortcode ?? ""}`.trim();
    if (!normalizedShortcode) return "";
    return new URL(encodeURIComponent(normalizedShortcode), getApiOriginUrl()).toString();
}

function normalizeGs1DigitalLinkBaseUri(baseUri, fallback = DEFAULT_GS1_DIGITAL_LINK_BASE_URI) {
    const raw = `${baseUri ?? ""}`.trim();
    if (!raw) return fallback;
    try {
        return new URL(raw).toString();
    } catch {
        return raw;
    }
}

function buildGs1DigitalLinkUri(aiEntries, baseUri = DEFAULT_GS1_DIGITAL_LINK_BASE_URI) {
    const entries = normalizeGs1AiEntries(aiEntries).filter((entry) => entry.value.length > 0);
    if (entries.length === 0) return "";
    const primary = entries.find((entry) => entry.ai === "01");
    if (!primary) return "";

    const used = new Set();
    const url = new URL(normalizeGs1DigitalLinkBaseUri(baseUri));
    const basePathParts = `${url.pathname || "/"}`
        .split("/")
        .filter((part) => part.length > 0)
        .map((part) => encodeURIComponent(decodeURIComponent(part)));
    url.search = "";
    url.hash = "";

    const pathParts = ["01", encodeURIComponent(primary.value)];
    used.add(primary);

    GS1_GTIN_QUALIFIER_ORDER.forEach((ai) => {
        const entry = entries.find((candidate) => candidate.ai === ai);
        if (!entry) return;
        pathParts.push(ai, encodeURIComponent(entry.value));
        used.add(entry);
    });

    url.pathname = `/${basePathParts.concat(pathParts).join("/")}`;

    entries.forEach((entry) => {
        if (used.has(entry)) return;
        url.searchParams.append(entry.ai, entry.value);
    });

    return url.toString();
}

function getQrCodeUrlFromApi(code) {
    if (!code) return null;
    const shortcode = code.shortcode ?? code.shortcodeid;
    if (!shortcode) return null;
    const encodedShortcode = encodeURIComponent(shortcode);
    const qrBase =
        code.qr?.baseurl ||
        code.qr?.baseUrl ||
        code.qrBaseUrl ||
        code.qr_baseurl;
    if (qrBase) {
        const normalizedBase = `${qrBase}`.replace(/\/+$/, "");
        if (/\/qr$/i.test(normalizedBase)) {
            return `${normalizedBase}/${encodedShortcode}`;
        }
        return `${normalizedBase}/qr/${encodedShortcode}`;
    }
    return `${process.env.VUE_APP_API_BASE_URL}qr/${encodedShortcode}`;
}

function getGtinUrlFromApi(code) {
    if (!code) return null;
    const shortcode = code.shortcode ?? code.shortcodeid;
    if (!shortcode) return null;
    const encodedShortcode = encodeURIComponent(shortcode);
    return `${process.env.VUE_APP_API_BASE_URL}gtin/${encodedShortcode}`;
}

function getCombinedBarcodeUrlFromApi(code) {
    if (!code) return null;
    const shortcode = code.shortcode ?? code.shortcodeid;
    if (!shortcode) return null;
    const encodedShortcode = encodeURIComponent(shortcode);
    return `${process.env.VUE_APP_API_BASE_URL}combined-barcode/${encodedShortcode}`;
}

function getGs1QrUrlFromApi(code) {
    if (!code?.gs1Support) return null;
    if (!resolveGs1DigitalLinkUri(code)) return null;
    const shortcode = code.shortcode ?? code.shortcodeid;
    if (!shortcode) return null;
    const encodedShortcode = encodeURIComponent(shortcode);
    return `${process.env.VUE_APP_API_BASE_URL}gs1/qr/${encodedShortcode}`;
}

function getGs1DataMatrixUrlFromApi(code) {
    if (!code?.gs1Support) return null;
    if (!code?.gs1?.elementString) return null;
    const shortcode = code.shortcode ?? code.shortcodeid;
    if (!shortcode) return null;
    const encodedShortcode = encodeURIComponent(shortcode);
    return `${process.env.VUE_APP_API_BASE_URL}gs1/datamatrix/${encodedShortcode}`;
}

function getCodeUrlByShortcode(shortcode) {
    return `${process.env.VUE_APP_API_BASE_URL}codes/${encodeURIComponent(shortcode)}`;
}

function getAnalyticsBaseUrl() {
    const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;
    if (apiBaseUrl) {
        const parsedApiBaseUrl = new URL(apiBaseUrl);
        parsedApiBaseUrl.pathname = "/";
        parsedApiBaseUrl.search = "";
        parsedApiBaseUrl.hash = "";
        return parsedApiBaseUrl.toString();
    }
    if (typeof window !== "undefined" && window.location?.origin) {
        return `${window.location.origin}/`;
    }
    return "https://qrexl.ink/";
}

function getCodeAnalyticsUrl(shortcode, filters = {}) {
    const baseUrl = new URL(getAnalyticsBaseUrl());
    const normalizedBasePath = baseUrl.pathname.endsWith("/")
        ? baseUrl.pathname
        : `${baseUrl.pathname}/`;
    baseUrl.pathname = `${normalizedBasePath}${encodeURIComponent(shortcode)}/a`;

    const query = new URLSearchParams();
    const supportedFilters = ["groupBy", "range", "country", "state", "city", "from", "to"];
    supportedFilters.forEach((key) => {
        const value = filters[key];
        if (value !== undefined && value !== null && `${value}`.length > 0) {
            query.set(key, value);
        }
    });
    query.set("json", "1");
    baseUrl.search = query.toString();
    return baseUrl.toString();
}

async function getCodeAnalyticsFromApi(shortcode, filters = {}) {
    const url = getCodeAnalyticsUrl(shortcode, filters);
    const res = await fetch(url, {
        headers: {
            Accept: "application/json",
        },
    });
    const contentType = res.headers.get("content-type") || "";
    if (!res.ok) {
        let message = `Analytics request failed (${res.status})`;
        try {
            if (contentType.includes("application/json")) {
                const errorData = await res.json();
                if (errorData?.error) {
                    message = errorData.error;
                }
            }
        } catch (err) {
            console.log(`analytics error parsing failed ${JSON.stringify(err)}`);
        }
        throw new Error(message);
    }
    if (!contentType.includes("application/json")) {
        throw new Error(`Analytics endpoint returned ${contentType || "an unexpected response type"}`);
    }
    return await res.json();
}

function getSingleCodeFromApi(data) {
    if (!data) return null;
    if (Array.isArray(data)) return data[0] ?? null;
    if (Array.isArray(data.codes)) return data.codes[0] ?? null;
    if (data.code && typeof data.code === "object") return data.code;
    if (typeof data === "object") return data;
    return null;
}

async function getAndNormalizeCodes(theUsers, expandUsers, userId) {
    return new Promise((resolve) => {
        let returnObj = {
            theUsers: theUsers,
            theCodes: [],
            theMessages: "",
        };
        try {
            let url = `codes`;
            if (userId) {
                url += `?userid=${encodeURIComponent(userId)}`;
            }
            apiFetchJson(url, {}, { auth: true })
                .then(async (data) => {
                    const apiCodes = Array.isArray(data)
                        ? data
                        : Array.isArray(data?.codes)
                            ? data.codes
                            : [];
                    if (data) {
                        console.log(`data count: ${apiCodes.length}`);
                        let removedCodes = [];
                        returnObj.theCodes = apiCodes.map(normalizeCodeFromApi).filter((code) => {
                            return (
                                code.codeid &&
                                !code.codeid.toLowerCase().includes("null")
                                //&&
                                //code.redirecturl &&
                                // !code.redirecturl.toLowerCase().includes("null")
                            );
                        });
                        //go get relevant users
                        if (returnObj.theCodes.length > 0) {
                            //loop through codes to find additional users
                            await Promise.all(
                                returnObj.theCodes.map(async (code) => {
                                    try {
                                        if (expandUsers) {
                                            //load all relevant users.
                                            let ownerUserId = code.owneruserid;
                                            let theOwnerUser = returnObj.theUsers.find((user) => {
                                                return user.id == ownerUserId;
                                            });
                                            if (theOwnerUser) {
                                                if (theOwnerUser.ownerUserId && theOwnerUser.ownerUserId != 0) {
                                                    let theOwnerOwnerUser = getUser(theOwnerUser.ownerUserId);
                                                    returnObj.theUsers.push(theOwnerOwnerUser);
                                                    code.hasManager = true;
                                                }
                                            } else {
                                                removedCodes.push(code.id);
                                            }
                                        } else {
                                            //only leave codes that relate to current user list
                                            let codeUsers = returnObj.theUsers.filter((user) => {
                                                return code.owneruserid == user.id; //|| code.owneruserid == user.owner_user_id
                                            });
                                            if (!codeUsers || codeUsers.length == 0) {
                                                removedCodes.push(code.id);
                                            }
                                        }
                                    } catch (err) {
                                        console.log(`review codes error ${JSON.stringify(err)}`);
                                        //returnObj.theMessages += `error analyzing codes @ codeid ${code.id} err ${JSON.stringify(err)}`
                                    }
                                })
                            );
                            if (removedCodes.length > 0) {
                                returnObj.theCodes = returnObj.theCodes.filter((code) => {
                                    return !removedCodes.includes(code.id);
                                });
                            }
                            if (returnObj.theCodes.length <= 0) {
                                returnObj.theMessages += "No Codes Loaded";
                            } else {
                                returnObj.theMessages += `${returnObj.theCodes.length} Codes Loaded.`;
                            }
                            resolve(returnObj);
                        } else {
                            returnObj.theMessages += `No Codes Loaded`;
                            resolve(returnObj);
                        }
                    }
                })
                .catch((err) => {
                    const msg = err && err.message ? err.message : JSON.stringify(err);
                    returnObj.theMessages += `Loading Codes Failed. ${msg}`;
                    console.log(msg);
                    resolve(returnObj);
                });
        } catch (err) {
            returnObj.theMessages += `error Loading Codes ${JSON.stringify(err)}`;
            console.log(JSON.stringify(err));
            resolve(returnObj);
        }
    });
}

/* doing imports in libs is somehow bad for vue3
function methodThatForcesUpdate() {
    const instance = getCurrentInstance();
    instance.proxy.forceUpdate();
}
*/

export {
    DEFAULT_GS1_DIGITAL_LINK_BASE_URI,
    createDefaultGs1AiEntries,
    normalizeGs1AiEntries,
    normalizeGs1DigitalLinkBaseUri,
    buildShortcodeBaseUri,
    parseGs1ElementStringToAiEntries,
    getGs1AiEntries,
    buildGs1FormAiEntries,
    buildGs1ElementString,
    buildGs1DigitalLinkUri,
    decycle,
    getUser,
    getUserFullNameFromApi,
    getAndNormalizeCodes,
    normalizeUserFromApi,
    normalizeCodeFromApi,
    getQrCodeUrlFromApi,
    getGtinUrlFromApi,
    getCombinedBarcodeUrlFromApi,
    getGs1QrUrlFromApi,
    getGs1DataMatrixUrlFromApi,
    getCodeUrlByShortcode,
    getCodeAnalyticsUrl,
    getCodeAnalyticsFromApi,
    getSingleCodeFromApi,
    //methodThatForcesUpdate,
};
